import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import { Bell, Radio, Lock, ChevronRight } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "ora";
  if (s < 3600) return `${Math.floor(s / 60)} min fa`;
  if (s < 86400) return `${Math.floor(s / 3600)} h fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function Notifiche() {
  const { isAuthenticated } = useAuth();
  const { tick } = useActivity();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    api.get("/activity").then((r) => { setItems(r.data.items || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, [isAuthenticated, tick]);

  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <BackButton className="mb-6" />
        <Lock className="h-10 w-10 text-zinc-600 mx-auto" />
        <h1 className="mt-4 font-heading text-3xl font-black uppercase text-zinc-50">Notifiche</h1>
        <p className="mt-2 text-zinc-400">Accedi come pilota iscritto per vedere le notifiche.</p>
        <Link to="/accedi" data-testid="notifiche-login-cta"
          className="mt-6 inline-block bg-yellow-400 text-zinc-950 font-bold text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-yellow-300 transition-colors duration-200">
          Accedi
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <h1 className="flex items-center gap-3 font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter text-zinc-50">
        <Bell className="h-8 w-8 text-yellow-400" /> Notifiche
      </h1>
      <p className="mt-2 text-zinc-400">Gli ultimi aggiornamenti su campionati, Gran Premi, iscrizioni e risultati.</p>

      <div className="mt-8 space-y-3" data-testid="notifiche-list">
        {loaded && items.length === 0 && (
          <p className="text-zinc-500 py-10 text-center" data-testid="notifiche-empty">Nessuna notifica per ora.</p>
        )}
        {items.map((n, i) => {
          const clickable = !!n.link_path;
          const inner = (
            <>
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-500/10 border border-cyan-500/30">
                <Radio className="h-4 w-4 text-cyan-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-zinc-100">{n.message}</p>
                <p className="mt-0.5 text-[11px] text-zinc-500">{timeAgo(n.created_at)}</p>
              </div>
              {clickable && <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500 self-center" />}
            </>
          );
          const cls = `flex items-start gap-3 border border-zinc-800 bg-zinc-900/50 px-5 py-4 ${clickable ? "cursor-pointer hover:border-yellow-400/60 hover:bg-zinc-900 transition-colors duration-200" : ""}`;
          return (
            <motion.div key={n.id}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
              data-testid={`notifica-${i}`}
              onClick={clickable ? () => navigate(n.link_path) : undefined}
              className={cls}>
              {inner}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
